import { useEffect, useState } from "react";
import { useApi } from "../../../hooks";

/**
 * Screenshots sit behind the authorised task API, so a bare <img src="..."> can
 * never load one — the browser sends no bearer token, and the plain
 * `/assets/:id` route returns a JSON-encoded SAS URL rather than image bytes.
 * Fetch the bytes from `/assets/:id/download` through the authed client and
 * hand the <img> an object URL instead.
 */
export const useScreenshotUrl = (
  taskId: string,
  activityId: string,
  screenshotId?: string,
  enabled: boolean = true
) => {
  const { get } = useApi();
  const [url, setUrl] = useState<string>("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // Clear eagerly so a slow or failing fetch never leaves the previously
    // selected screenshot on screen.
    setUrl("");
    setFailed(false);

    if (!enabled || !taskId || !activityId || !screenshotId) {
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    get<Blob>(
      `/tasks/${taskId}/activities/${activityId}/assets/${screenshotId}/download`,
      { responseType: "blob" }
    )
      .then((blob) => {
        if (cancelled) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.error("Error fetching screenshot:", error);
        setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
    // `get` is rebuilt on every render of AuthApiProvider, so depending on it
    // would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, activityId, screenshotId, enabled]);

  return { url, failed };
};
